import { useEffect, useState } from "react";
import { api, formatCHF, formatDate } from "../lib/api";
import { StatusBadge } from "../components/nivara/StatusBadge";
import { GroupTimeline } from "../components/nivara/GroupTimeline";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Calendar, Clock, Gavel, Wallet, CheckCircle2 } from "lucide-react";

const AVATARS = [
  "https://static.prod-images.emergentagent.com/jobs/a8eea4b1-677b-4892-819c-00b0a2a4bc7e/images/fdc443e0d5b3cc1dafb24f951ffea432884100cbd82c05db99c2c633323363ca.png",
  "https://static.prod-images.emergentagent.com/jobs/a8eea4b1-677b-4892-819c-00b0a2a4bc7e/images/e98fe0718e31b4a1b86e69894696fb95e6d0d0c72a6647024bbc688edd5ca54d.png",
];

export default function ParticipantDashboard() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [current, setCurrent] = useState(null);
  const [payments, setPayments] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [auctions, setAuctions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data: gs } = await api.get("/groups");
        setGroups(gs);
        if (gs.length > 0) {
          const g = gs[0];
          const [tl, cur, parts, pays, pos, aucs] = await Promise.all([
            api.get(`/groups/${g.id}/timeline`),
            api.get(`/groups/${g.id}/contributions/current`),
            api.get(`/groups/${g.id}/participants`),
            api.get(`/payments?group_id=${g.id}`),
            api.get(`/payouts?group_id=${g.id}`),
            api.get(`/auctions?group_id=${g.id}`),
          ]);
          setTimeline(tl.data);
          setCurrent(cur.data);
          setParticipants(parts.data);
          setPayments(pays.data);
          setPayouts(pos.data);
          setAuctions(aucs.data);
        }
      } catch {
        // ignore
      }
    })();
  }, []);

  const group = groups[0];
  const me = participants.find((p) => p.is_self);
  const myPayments = payments.filter((p) => p.user_id === user?.id);
  const hasReceived = me?.has_received;
  const nextAuction = auctions.find((a) => a.status === "open" || a.status === "scheduled");

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
      <div className="mb-8">
        <div className="nv-label">{t("dashboard.welcome")}</div>
        <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-[#0F4C3A] mt-2" data-testid="participant-dashboard-title">
          {t("dashboard.hello")} {user?.name}
        </h1>
      </div>

      {!group && (
        <div className="nv-border bg-white p-10 rounded-md text-center">
          <div className="font-heading text-xl text-[#0F4C3A]">{t("dashboard.no_group_title")}</div>
          <p className="text-[#475569] mt-3 max-w-md mx-auto">
            {t("dashboard.no_group_text")}
          </p>
        </div>
      )}

      {group && (
        <>
          {/* Key metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-[#E2E8E4] rounded-md overflow-hidden mb-8">
            <MetricCell label={t("dashboard.group")} value={group.name} />
            <MetricCell label={t("dashboard.share")} value={me ? `#${me.share_number}` : "—"} mono />
            <MetricCell
              label={t("dashboard.current_month")}
              value={current ? `${current.month} / ${group.shares}` : "—"}
              mono
            />
            <MetricCell
              label={t("dashboard.monthly_amount")}
              value={current ? `CHF ${formatCHF(current.amount_per_share)}` : "—"}
              mono
              highlight
            />
          </div>

          {/* Current month details */}
          {current && (
            <div className="nv-border bg-white rounded-md p-6 md:p-8 mb-8" data-testid="current-month-card">
              <div className="flex items-start justify-between flex-wrap gap-6">
                <div>
                  <div className="nv-label">{t("dashboard.month")} {current.month}</div>
                  <div className="font-heading text-2xl font-medium text-[#0F4C3A] mt-2">
                    {current.confirmed ? t("dashboard.confirmed") : t("dashboard.pending")}
                  </div>
                  <div className="flex items-center gap-6 mt-4 text-sm text-[#475569] flex-wrap">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {t("dashboard.payment_due")} {formatDate(current.due_date)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {t("dashboard.payout_due")} {formatDate(current.payout_due_date)}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <div className="nv-label">{t("dashboard.your_amount")}</div>
                  <div className="font-mono-nv text-4xl font-semibold text-[#0F4C3A] mt-2">
                    CHF {formatCHF(current.amount_per_share)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bid info */}
          {nextAuction && !hasReceived && (
            <div className="nv-border bg-[#0F4C3A] text-white rounded-md p-6 mb-8" data-testid="active-auction-card">
              <div className="flex items-center gap-3">
                <Gavel className="w-5 h-5 text-[#D4AF37]" />
                <div className="font-heading text-xl font-medium">{t("dashboard.active_auction")}</div>
              </div>
              <div className="mt-2 text-[#CBE8DA]">
                {t("dashboard.month")} {nextAuction.month} – {t("dashboard.active_auction_text")}
              </div>
              <Link
                to={`/my/auctions`}
                data-testid="participant-goto-auction"
                className="nv-btn-gold inline-flex items-center mt-5 px-5 py-2.5 rounded-md font-medium"
              >
                {t("dashboard.go_to_auction")}
              </Link>
            </div>
          )}

          {hasReceived && (
            <div className="nv-border bg-[#E8F5E9] rounded-md p-5 mb-8 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#2E7D32]" />
              <div>
                <div className="font-medium text-[#2E7D32]">{t("dashboard.has_received_title")}</div>
                <div className="text-sm text-[#1E5E22]">{t("dashboard.has_received_text", { month: me?.received_month })}</div>
              </div>
            </div>
          )}

          {/* Timeline */}
          {timeline.length > 0 && (
            <div className="mb-8">
              <GroupTimeline timeline={timeline} currentMonth={current?.month || 1} />
            </div>
          )}

          {/* Anonymous participants grid */}
          <div className="mb-8">
            <h2 className="font-heading text-xl font-medium text-[#0F4C3A] mb-4">{t("dashboard.group_title", { count: participants.length })}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {participants.map((p) => (
                <div
                  key={p.id}
                  data-testid={`participant-card-${p.share_number}`}
                  className={`nv-border rounded-md p-4 text-center ${p.is_self ? "border-[#D4AF37] bg-[#F0FDF4]" : "bg-white"}`}
                >
                  <img
                    src={AVATARS[(p.share_number - 1) % AVATARS.length]}
                    alt="avatar"
                    className="w-12 h-12 mx-auto rounded-full mb-2"
                  />
                  <div className="font-mono-nv text-sm font-semibold text-[#0F4C3A]">
                    #{p.share_number}
                  </div>
                  <div className="text-xs text-[#475569] mt-1">
                    {p.is_self ? t("dashboard.you") : `${t("role.participant")} #${p.share_number}`}
                  </div>
                  {p.has_received && (
                    <div className="mt-2 text-[10px] tracking-wider uppercase font-semibold text-[#2E7D32]">{t("dashboard.received")}</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* My payments */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="nv-border bg-white rounded-md">
              <div className="p-5 border-b border-[#E2E8E4] flex items-center justify-between">
                <div>
                  <div className="nv-label">{t("nav.payments")}</div>
                  <div className="font-heading text-lg font-medium text-[#0F4C3A]">{t("dashboard.my_payments")}</div>
                </div>
                <Wallet className="w-5 h-5 text-[#0F4C3A]" />
              </div>
              <div>
                {myPayments.length === 0 ? (
                  <div className="p-8 text-center text-[#94A3B8] text-sm">{t("dashboard.no_payments")}</div>
                ) : (
                  myPayments.slice(0, 5).map((p) => (
                    <div key={p.id} className="px-5 py-3 border-b border-[#E2E8E4] last:border-b-0 flex items-center justify-between hover:bg-[#F8FAF9]">
                      <div>
                        <div className="font-mono-nv text-sm text-[#0F4C3A]">{t("dashboard.month")} {p.month}</div>
                        <div className="text-xs text-[#94A3B8]">{formatDate(p.paid_at)}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono-nv text-sm font-semibold text-[#0F4C3A]">CHF {formatCHF(p.amount)}</div>
                        <div className="mt-1"><StatusBadge status={p.status} /></div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="nv-border bg-white rounded-md">
              <div className="p-5 border-b border-[#E2E8E4] flex items-center justify-between">
                <div>
                  <div className="nv-label">{t("dashboard.payouts")}</div>
                  <div className="font-heading text-lg font-medium text-[#0F4C3A]">{t("dashboard.my_payouts_history")}</div>
                </div>
              </div>
              <div>
                {payouts.length === 0 ? (
                  <div className="p-8 text-center text-[#94A3B8] text-sm">{t("dashboard.no_payouts")}</div>
                ) : (
                  payouts.map((p) => (
                    <div key={p.id} className="px-5 py-3 border-b border-[#E2E8E4] last:border-b-0 flex items-center justify-between">
                      <div>
                        <div className="font-mono-nv text-sm text-[#0F4C3A]">{t("dashboard.month")} {p.month}</div>
                        <div className="text-xs text-[#94A3B8]">{formatDate(p.released_at)}</div>
                      </div>
                      <div className="font-mono-nv text-sm font-semibold text-[#2E7D32]">
                        + CHF {formatCHF(p.amount)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MetricCell({ label, value, mono, highlight }) {
  return (
    <div className={`p-5 md:p-6 border-r border-b md:border-b-0 border-[#E2E8E4] last:border-r-0 ${highlight ? "bg-[#0F4C3A] text-white" : "bg-white"}`}>
      <div className={`nv-label ${highlight ? "!text-[#D4AF37]" : ""}`}>{label}</div>
      <div className={`mt-3 ${mono ? "font-mono-nv" : "font-heading"} ${highlight ? "text-2xl" : "text-xl md:text-2xl"} font-semibold ${highlight ? "text-white" : "text-[#0F4C3A]"}`}>
        {value}
      </div>
    </div>
  );
}
